import React from 'react';
import { QrCode, Shield, Zap, CheckCircle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/user/Navbar';
import Footer from '../../../components/user/Footer';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const QRPaymentSolutions: React.FC = () => {
  const [heroRef, heroVisible] = useScrollAnimation();
  const [featuresRef, featuresVisible] = useScrollAnimation();
  const [benefitsRef, benefitsVisible] = useScrollAnimation();
  const [ctaRef, ctaVisible] = useScrollAnimation();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section ref={heroRef} className="pt-32 pb-20 px-4 bg-gradient-to-br from-primary-50 to-white">
        <div className={`max-w-7xl mx-auto transition-all duration-700 ${
          heroVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
        }`}>
          <Link 
            to="/services" 
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Services
          </Link>
          <div className="flex items-center gap-6 mb-6">
            <div className="bg-primary-100 p-4 rounded-2xl">
              <QrCode className="text-primary-600" size={64} />
            </div>
            <div>
              <h1 className="text-5xl md:text-6xl font-bold text-secondary-900 mb-4 tracking-tight">
                QR Payment Solutions
              </h1>
              <p className="text-xl text-secondary-600">
                Accept and process payments instantly with secure QR codes
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-secondary-900 mb-6">
                Revolutionize Your Payment Experience
              </h2>
              <p className="text-lg text-secondary-600 leading-relaxed mb-6">
                Our QR Payment Solutions provide businesses with a fast, secure, and cost-effective 
                way to accept digital payments. Whether you're a small retail store or a large enterprise, 
                our flexible QR code system adapts to your needs.
              </p>
              <p className="text-lg text-secondary-600 leading-relaxed">
                Support for both static and dynamic QR codes gives you the flexibility to process 
                one-time payments, recurring transactions, and custom payment flows. All transactions 
                are encrypted and processed in real-time, ensuring security and instant confirmation.
              </p>
            </div>
            <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-12 text-white shadow-soft">
              <Shield size={48} className="mb-6" />
              <h3 className="text-2xl font-bold mb-4">Bank-Level Security</h3>
              <p className="text-lg text-white/90 leading-relaxed">
                Every transaction is protected by industry-standard encryption and security protocols. 
                Your customers' payment data is never stored, ensuring complete privacy and security.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresRef} className="py-20 px-4 bg-secondary-50">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-700 ${
            featuresVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
          }`}>
            <h2 className="text-4xl font-bold text-secondary-900 mb-4">
              Key Features
            </h2>
            <p className="text-xl text-secondary-600">
              Everything you need for seamless QR payments
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <QrCode size={40} />,
                title: 'Static & Dynamic QR Codes',
                description: 'Generate both static QR codes for fixed amounts and dynamic QR codes for variable payments. Perfect for different business scenarios.'
              },
              {
                icon: <Zap size={40} />,
                title: 'Instant Payment Processing',
                description: 'Process payments in real-time with instant confirmation. No waiting, no delays - just seamless transactions.'
              },
              {
                icon: <Shield size={40} />,
                title: 'Secure Transactions',
                description: 'Bank-level encryption and security protocols protect every transaction. PCI DSS compliant infrastructure.'
              },
              {
                icon: <CheckCircle size={40} />,
                title: 'Multi-currency Support',
                description: 'Accept payments in multiple currencies. Perfect for businesses serving international customers.'
              },
              {
                icon: <Zap size={40} />,
                title: 'Real-time Notifications',
                description: 'Get instant notifications for every payment. Keep track of your transactions in real-time.'
              },
              {
                icon: <CheckCircle size={40} />,
                title: 'Transaction History',
                description: 'Complete transaction records with detailed information. Export reports for accounting and analysis.'
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className={`bg-white p-8 rounded-xl border-2 border-secondary-100 hover:border-primary-500 hover:shadow-soft transition-all duration-500 group hover:scale-105 hover:-translate-y-2 ${
                  featuresVisible 
                    ? `animate-fade-in-up opacity-100` 
                    : 'opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="text-primary-500 mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-secondary-900 mb-3 group-hover:text-primary-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-secondary-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section ref={benefitsRef} className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-700 ${
            benefitsVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
          }`}>
            <h2 className="text-4xl font-bold text-secondary-900 mb-4">
              Why Choose Our QR Payment Solutions?
            </h2>
            <p className="text-xl text-secondary-600">
              Benefits that make a difference
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Lower Transaction Costs',
                description: 'Reduce payment processing fees compared to traditional card payments. Save money on every transaction.'
              },
              {
                title: 'Faster Checkout',
                description: 'Customers can pay in seconds with a simple QR scan. Reduce wait times and improve customer satisfaction.'
              },
              {
                title: 'No Hardware Required',
                description: 'Start accepting payments immediately without expensive POS terminals. All you need is a smartphone or tablet.'
              },
              {
                title: '24/7 Availability',
                description: 'Accept payments anytime, anywhere. Your QR codes work 24/7, even when your business is closed.'
              },
              {
                title: 'Easy Integration',
                description: 'Simple setup process with comprehensive documentation. Get started in minutes, not days.'
              },
              {
                title: 'Scalable Solution',
                description: 'Grow from a small business to an enterprise without changing your payment system. Scales with your needs.'
              }
            ].map((benefit, index) => (
              <div 
                key={index}
                className={`bg-white p-8 rounded-xl border-2 border-secondary-100 hover:border-primary-500 transition-all duration-500 ${
                  benefitsVisible 
                    ? `animate-fade-in-up opacity-100` 
                    : 'opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <h3 className="text-xl font-bold text-secondary-900 mb-3 flex items-center">
                  <CheckCircle className="text-primary-500 mr-3" size={24} />
                  {benefit.title}
                </h3>
                <p className="text-secondary-600">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="py-20 px-4 bg-primary-500">
        <div className={`max-w-4xl mx-auto text-center text-white transition-all duration-700 ${
          ctaVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
        }`}>
          <h2 className="text-4xl font-bold mb-6 tracking-tight">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Start accepting QR payments today and transform your business
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="bg-white text-primary-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-secondary-50 transition-all duration-300 shadow-xl hover:scale-110 hover:-translate-y-1"
            >
              Contact Sales
            </Link>
            <Link
              to="/services"
              className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-primary-600 transition-all duration-300 hover:scale-110 hover:-translate-y-1"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default QRPaymentSolutions;

